import React, { useState, useEffect, useMemo } from 'react';
import { Button, Row, Col, Select, message, Spin, Empty, Typography } from 'antd';
import AppLayout from '../../../components/layout/AppLayout';
import ProjectCard from '../../../components/cards/projects/ProjectCard';
import ProjectModal from './ProjectModal';
import { TProject, TProjectLike } from '../../../models/project/project';
import { useProjectApi } from '../../../api/projects/projects';
import { useReferential } from '../../../context/ReferentialContext';
import { CODIFICATION_TYPE } from '../../../constant/codification';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

const ProjectList: React.FC = () => {
    const { t } = useTranslation();
    const { getUserAppId } = useTilmiAuth();
    const { getProjectList, getLikesByUserId } = useProjectApi();

    const [projects, setProjects] = useState<TProject[]>([]);
    const [likes, setLikes] = useState<TProjectLike[]>([]);
    const [types, setTypes] = useState<number[]>([]);
    const { getCodificationsByCodeType } = useReferential();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedType, setSelectedType] = useState<string>('');
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [editingProject, setEditingProject] = useState<TProject | null>(null);

    const palette = useMemo(
        () => ({
            primary: '#2D9CC9',
            secondary: '#8BBE1E',
            background: 'linear-gradient(135deg, rgba(45,156,201,0.08), rgba(139,190,30,0.06))',
            border: 'rgba(45,156,201,0.18)',
            surface: '#ffffff',
            muted: 'rgba(12, 24, 42, 0.65)',
        }),
        []
    );

    const projectTypeCodifs = useMemo(
        () => getCodificationsByCodeType(CODIFICATION_TYPE.PROJECT_TYPE),
        [getCodificationsByCodeType]
    );

    useEffect(() => {
        setIsLoading(true);
        Promise.all([getProjectList(), getLikesByUserId(getUserAppId())])
            .then((results) => {
                const [studentProjectsData, likesData] = results;
                const proj = studentProjectsData.data || [];
                setProjects(proj);
                setLikes(likesData.data || []);

                setProjects(proj);
                const uniqueTypes = Array.from(
                    new Set(proj.map(p => p.project_type_id).filter(id => id != null))
                ) as number[];
                setTypes(uniqueTypes);
            })
            .catch(err => message.error(err.message))
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line
    }, []);

    const handleSaveProject = (updatedProject: TProject) => {
        setProjects(prev =>
            prev.some(p => p.id === updatedProject.id)
                ? prev.map(p => (p.id === updatedProject.id ? updatedProject : p))
                : [updatedProject, ...prev]
        );
        setIsModalVisible(false);
        setEditingProject(null);
    };

    return (
        <AppLayout title={t('project.studentPage.title')} loading={isLoading}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 24,
                    padding: '24px 16px 32px',
                }}
            >
                <section
                    style={{
                        borderRadius: 28,
                        padding: '28px 24px',
                        background: palette.background,
                        border: `1px solid ${palette.border}`,
                        boxShadow: '0 20px 45px rgba(7, 24, 38, 0.12)',
                        backdropFilter: 'blur(14px)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                        }}
                    >
                        <Typography.Title level={3} style={{ margin: 0 }}>
                            {t('project.studentPage.title')}
                        </Typography.Title>
                        <Typography.Text style={{ color: palette.muted }}>
                            {t(
                                'project.studentPage.subtitle',
                                'Crée, partage et fais évoluer tes projets Tilmi.'
                            )}
                        </Typography.Text>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 16,
                            justifyContent: 'space-between',
                        }}
                    >
                        <Button
                            type="primary"
                            onClick={() => {
                                setEditingProject(null);
                                setIsModalVisible(true);
                            }}
                            style={{
                                borderRadius: 999,
                                padding: '0 22px',
                                height: 48,
                                border: 'none',
                                background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
                                boxShadow: '0 18px 36px rgba(45,156,201,0.25)',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                            }}
                        >
                            {t('project.studentPage.createProject')}
                        </Button>
                        <Select
                            style={{ minWidth: 220 }}
                            placeholder={t('project.studentPage.filterByType')}
                            onChange={(value) => setSelectedType(value)}
                            value={selectedType || undefined}
                            size="large"
                        >
                            {types.map(typeId => {
                                const codif = projectTypeCodifs.find(c => c.id === typeId);
                                return (
                                    <Option key={typeId} value={typeId}>
                                        {codif?.label}
                                    </Option>
                                );
                            })}
                        </Select>
                    </div>
                </section>

                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        {projects.filter(prj => (selectedType ? prj.project_type_id === Number(selectedType) : true)).length === 0 ? (
                            <div
                                style={{
                                    borderRadius: 28,
                                    padding: '40px 16px',
                                    background: '#fff',
                                    border: `1px solid ${palette.border}`,
                                    boxShadow: '0 16px 32px rgba(7, 24, 38, 0.08)',
                                }}
                            >
                                <Empty description={t('project.studentPage.empty', 'Aucun projet pour ce filtre.')} />
                            </div>
                        ) : (
                            <Row gutter={[20, 24]}>
                                {projects
                                    .filter(prj => (selectedType ? prj.project_type_id === Number(selectedType) : true))
                                    .map(prj => (
                                        <Col key={prj.id} xs={24} sm={12} md={8} lg={6}>
                                            <ProjectCard
                                                studentProject={prj}
                                                likedStatus={likes}
                                                onEditProject={(p) => {
                                                    setEditingProject(p);
                                                    setIsModalVisible(true);
                                                }}
                                            />
                                        </Col>
                                    ))}
                            </Row>
                        )}
                    </>
                )}

                {isModalVisible && (
                    <ProjectModal
                        editingProject={editingProject}
                        onClose={() => setIsModalVisible(false)}
                        onSave={handleSaveProject}
                    />
                )}
            </div>
        </AppLayout>
    );
};

export default ProjectList;
